/**
 * Price range on a shared catalogue: "up to", "between" or "and above", on the price the viewer sees.
 * Lives in the URL (?minPrice=&maxPrice=) so Back, refresh and a shared link keep it. Bounds are inclusive.
 */
export type PriceRangeKind = "under" | "between" | "above";

export interface PriceRange {
  min?: number;
  max?: number;
}

const positive = (raw: string | null): number | undefined => {
  if (raw == null || raw.trim() === "") return undefined;
  const n = Number(raw.replace(/[,₹\s]/g, ""));
  return Number.isFinite(n) && n > 0 ? Math.round(n) : undefined;
};

export function priceRangeFromSearch(search: string): PriceRange {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const min = positive(params.get("minPrice"));
  const max = positive(params.get("maxPrice"));
  if (min != null && max != null && min > max) return {};
  return { min, max };
}

export function hasPriceRange(range: PriceRange): boolean {
  return range.min != null || range.max != null;
}

export function priceRangeKind(range: PriceRange): PriceRangeKind {
  if (range.min != null && range.max != null) return "between";
  if (range.min != null) return "above";
  return "under";
}

/** Returns params with the range set (or removed) and the page reset, since results change. */
export function withPriceRange(params: URLSearchParams, range: PriceRange): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete("minPrice");
  next.delete("maxPrice");
  next.delete("page");
  if (range.min != null) next.set("minPrice", String(range.min));
  if (range.max != null) next.set("maxPrice", String(range.max));
  return next;
}

export function rupees(n: number): string {
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n)}`;
}

export function describePriceRange(range: PriceRange): string {
  if (range.min != null && range.max != null) return `${rupees(range.min)} – ${rupees(range.max)}`;
  if (range.max != null) return `Up to ${rupees(range.max)}`;
  if (range.min != null) return `${rupees(range.min)} and above`;
  return "Any price";
}

/** Range from the form, or an error message. Empty fields mean "no limit" for that side. */
export function rangeFromInputs(kind: PriceRangeKind, minRaw: string, maxRaw: string): PriceRange | string {
  const read = (raw: string): number | undefined | "bad" => {
    const t = raw.replace(/[,₹\s]/g, "");
    if (!t) return undefined;
    if (!/^\d+(\.\d+)?$/.test(t)) return "bad";
    const n = Math.round(Number(t));
    return n > 0 ? n : undefined;
  };
  const min = kind === "under" ? undefined : read(minRaw);
  const max = kind === "above" ? undefined : read(maxRaw);
  if (min === "bad" || max === "bad") return "Enter an amount in rupees.";
  if (kind === "under" && max == null) return "Enter the highest price.";
  if (kind === "above" && min == null) return "Enter the lowest price.";
  if (kind === "between" && (min == null || max == null)) return "Enter both prices.";
  if (min != null && max != null && min > max) return "The first price must be lower than the second.";
  return { min, max };
}

/** Quick amounts, rounded to friendly steps around the prices in view. */
export function quickAmounts(prices: number[]): number[] {
  const sorted = prices.filter((p) => p > 0).sort((a, b) => a - b);
  if (sorted.length < 2) return [10000, 20000, 30000, 50000];
  const step = (n: number) => (n >= 100000 ? 10000 : n >= 20000 ? 5000 : 1000);
  const round = (n: number) => Math.max(step(n), Math.round(n / step(n)) * step(n));
  const picks = [0.25, 0.5, 0.75].map((q) => round(sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))]));
  return Array.from(new Set(picks)).slice(0, 3);
}
